/**
 * RenewalsBoard: a sales kanban for re-servicing existing (certified)
 * properties before their assurance lapses. Split from the exception
 * (Portfolio) board, which is the road to first certification; this is
 * everything after.
 *
 * Columns: the un-contacted properties are split across three temperature
 * bands, so they flow Expiring soon -> Due soon -> Expired on their own as
 * the clock runs. Once you act on one it moves into the manual sales stages
 * (Contacted, Quoted), then Dormant for ones parked. A property appears once
 * it is certified and inside the renewal window; when it is re-serviced it
 * cools to green and drops off the board. Each card is tinted by its
 * temperature so urgency reads at a glance in any column.
 *
 * Cards copy the Service Logs board card shape and use canonical classes only
 * (.ops-board*, .ops-board-card--temp-* tints, .ops-board-card-header/
 * -customer/-amount/-meta/-actions, .ops-board-action, TierBadge). The only
 * inline style is the column header's data-driven top-border colour, the same
 * documented exception PortfolioBoard uses.
 */

import { useEffect, useMemo, useState } from 'react';
import { formatDateMedium } from '../../../../utils/formatters';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useToast } from '../../../../components/Toast';
import { useConfig } from '../../../../contexts/ConfigContext';
import {
  computeTemperature,
  RENEWAL_COLUMNS,
  DORMANT_STAGE,
  TIER_FREQUENCIES,
  TierBadge,
  type Temperature,
} from './constants';

interface RenewalsBoardProps {
  supabase: SupabaseClient;
  properties: Array<Record<string, unknown>>;
  openProperty: (id: string) => void;
  onRefresh: () => void | Promise<void>;
  /** Edit permission. View-level users see the cards but no action buttons. */
  canEdit: boolean;
}

interface RenewalCard {
  id: string;
  name: string;
  columnKey: string;
  temp: Temperature;
  expiresLabel: string;
  annual: number | null;
  tier: string;
  managerName: string;
}

function daysToExpiry(assuredUntil: string | null): number | null {
  if (!assuredUntil) return null;
  return Math.floor((new Date(assuredUntil).getTime() - Date.now()) / 86400000);
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function formatDate(d: string): string {
  return formatDateMedium(d);
}

export default function RenewalsBoard({ supabase, properties, openProperty, onRefresh, canEdit }: RenewalsBoardProps) {
  const { showToast } = useToast();
  const { formatCurrency } = useConfig();
  const [busyId, setBusyId] = useState<string | null>(null);
  // Annual value per property = latest finalised service total x services/year
  // for the tier. Loaded separately; cards show it once it arrives.
  const [annualById, setAnnualById] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const ids = properties.filter((p) => str(p.certified_since)).map((p) => p.id as string).filter(Boolean);
      if (ids.length === 0) { setAnnualById({}); return; }
      const { data } = await supabase
        .from('service_logs')
        .select('property_id, total_amount, service_date, status')
        .in('property_id', ids)
        .in('status', ['paid', 'sent', 'overdue']);
      if (cancelled) return;
      const latest: Record<string, { date: string; total: number }> = {};
      for (const r of (data ?? []) as Array<{ property_id: string; total_amount: number; service_date: string }>) {
        if (!r.property_id) continue;
        const cur = latest[r.property_id];
        const date = r.service_date ?? '';
        if (!cur || date > cur.date) latest[r.property_id] = { date, total: Number(r.total_amount) || 0 };
      }
      const annual: Record<string, number> = {};
      for (const p of properties) {
        const id = p.id as string;
        const l = latest[id];
        if (!l) continue;
        const interval = TIER_FREQUENCIES[str(p.hospitality_tier) ?? 'assured'] ?? 365;
        annual[id] = l.total * Math.max(1, Math.round(365 / interval));
      }
      setAnnualById(annual);
    }
    void load();
    return () => { cancelled = true; };
  }, [supabase, properties]);

  // A certified property in the renewal window (temperature yellow/orange/red)
  // sits in its sales-stage column, or, if not yet worked, in its temperature
  // column. Green/healthy properties stay off the board (renewal done).
  const cardsByColumn = useMemo<Record<string, RenewalCard[]>>(() => {
    const empty: Record<string, RenewalCard[]> = {};
    for (const c of RENEWAL_COLUMNS) empty[c.key] = [];
    for (const raw of properties) {
      if (!str(raw.certified_since)) continue; // never certified -> exception board
      const assuredUntil = str(raw.assured_until);
      const temp = computeTemperature(assuredUntil);
      if (temp !== 'yellow' && temp !== 'orange' && temp !== 'red') continue; // green/undated -> off

      const stage = str(raw.renewal_stage);
      // Worked stages take their own column; un-worked sit in the temperature band.
      const columnKey = stage === 'dormant' || stage === 'contacted' || stage === 'quoted' ? stage : temp;
      const days = daysToExpiry(assuredUntil);
      const expiresLabel = !assuredUntil
        ? ''
        : days !== null && days < 0
          ? `Expired ${formatDate(assuredUntil)}`
          : `Expires ${formatDate(assuredUntil)}${days !== null ? ` (${days}d)` : ''}`;
      const mgr = raw.manager as { first_name?: string; last_name?: string } | null;
      (empty[columnKey] ??= []).push({
        id: raw.id as string,
        name: str(raw.property_name) ?? 'Unnamed property',
        columnKey,
        temp,
        expiresLabel,
        annual: annualById[raw.id as string] ?? null,
        tier: str(raw.hospitality_tier) ?? '',
        managerName: mgr ? `${mgr.first_name ?? ''} ${mgr.last_name ?? ''}`.trim() : '',
      });
    }
    return empty;
  }, [properties, annualById]);

  async function setStage(id: string, stage: string | null) {
    if (busyId) return;
    setBusyId(id);
    const { error } = await supabase.from('hosp_properties').update({ renewal_stage: stage }).eq('id', id);
    setBusyId(null);
    if (error) { showToast(error.message, 'error'); return; }
    await onRefresh();
  }

  const total = RENEWAL_COLUMNS.reduce((n, c) => n + (cardsByColumn[c.key]?.length ?? 0), 0);

  if (total === 0) {
    return <div className="ops-board-empty">No properties up for renewal. Everything assured is current.</div>;
  }

  return (
    <div className="ops-board">
      {RENEWAL_COLUMNS.map((col) => {
        const cards = cardsByColumn[col.key] ?? [];
        // Temperature columns are the un-contacted pool: advance them to
        // Contacted. Contacted advances to Quoted. Quoted is the end of the
        // worked path; Dormant is parked.
        const isTempCol = col.key === 'yellow' || col.key === 'orange' || col.key === 'red';
        const nextKey = isTempCol ? 'contacted' : col.key === 'contacted' ? 'quoted' : null;
        return (
          <div key={col.key} className="ops-board-column">
            <div className="ops-board-column-header" style={{ borderTopColor: col.color }}>
              <div className="ops-board-column-title-group">
                <span className="ops-board-column-title">{col.label}</span>
                <span className="ops-board-column-count">{cards.length}</span>
              </div>
            </div>
            <div className="ops-board-column-body">
              {cards.map((card) => {
                const busy = busyId === card.id;
                return (
                  <div
                    key={card.id}
                    className={`ops-board-card ops-board-card--temp-${card.temp}`}
                    onClick={() => openProperty(card.id)}
                  >
                    <div className="ops-board-card-header">
                      <span className="ops-board-card-customer">{card.name}</span>
                      {card.annual !== null && (
                        <span className="ops-board-card-amount">{formatCurrency(card.annual)}/yr</span>
                      )}
                    </div>
                    <div className="ops-board-card-meta">
                      {card.tier && <TierBadge tier={card.tier} />}
                      {card.expiresLabel && <span>{card.expiresLabel}</span>}
                    </div>
                    {card.managerName && (
                      <div className="ops-board-card-meta">
                        <span className="sl-meta-light">{card.managerName}</span>
                      </div>
                    )}
                    {canEdit && (
                    <div className="ops-board-card-actions" onClick={(e) => e.stopPropagation()}>
                      {nextKey && (
                        <button
                          className="ops-board-action ops-board-action-primary"
                          disabled={busy}
                          onClick={() => void setStage(card.id, nextKey)}
                        >
                          Mark {nextKey}
                        </button>
                      )}
                      {col.key !== 'dormant' ? (
                        <button className="ops-board-action" disabled={busy} onClick={() => void setStage(card.id, DORMANT_STAGE)}>
                          Dormant
                        </button>
                      ) : (
                        <button className="ops-board-action" disabled={busy} onClick={() => void setStage(card.id, null)}>
                          Revive
                        </button>
                      )}
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
